<template>
  <div class="floating-lights-bubble fixed left-4 z-50" :style="{ top: topOffset }">
    <!-- Unified Bubble that morphs between states -->
    <Bubble
      :expanded="isExpanded"
      :expanded-width="750"
      :collapsed-size="48"
      id="lights-bubble"
      origin="top-left"
      :class="{ 'cursor-pointer': !isExpanded }"
      @click="handleBubbleClick"
    >
      <!-- Collapsed State Content -->
      <div
        v-if="!isExpanded"
        class="h-full flex items-center justify-center"
      >
        <Lightbulb class="h-5 w-5 text-primary" />
      </div>

      <!-- Expanded State Content -->
      <template v-if="isExpanded">
        <!-- Header -->
        <BubbleHeader
          title="Lights"
          @close="isExpanded = false"
        >
          <template #icon>
            <Lightbulb class="h-4 w-4 text-primary" />
          </template>
        </BubbleHeader>

        <!-- Content -->
        <BubbleContent class="overflow-y-auto">
          <SceneLightsCanvas />
        </BubbleContent>

        <!-- Footer with Manage Lights -->
        <BubbleFooter>
          <Button
            @click.stop="showBridgeModal = true"
            variant="outline"
            class="w-full text-xs"
          >
            <Settings class="w-3 h-3 mr-1.5" />
            {{ hasLights ? 'Manage Lights' : 'Add Lights' }}
          </Button>
        </BubbleFooter>
      </template>
    </Bubble>

    <!-- Bridge Management Modal -->
    <LightBridgeManagementModal v-model:open="showBridgeModal" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Bubble, BubbleHeader, BubbleContent, BubbleFooter } from '@/components/ui/bubble'
import { Platform } from '@/lib/platform'
import { Lightbulb, Settings } from 'lucide-vue-next'
import { useHueBridgesStore } from '@/stores/hueBridges'
import { useLightsStore } from '@/stores/lights'
import SceneLightsCanvas from '@/components/scenes/lights/SceneLightsCanvas.vue'
import LightBridgeManagementModal from '@/components/integrations/LightBridgeManagementModal.vue'

const hueBridgesStore = useHueBridgesStore()
const lightsStore = useLightsStore()

const isExpanded = ref(false)
const showBridgeModal = ref(false)

// Add extra top padding on Tauri to account for traffic lights
const topOffset = computed(() => Platform.isDesktop() ? '2.5rem' : '1rem')

// Handle bubble click - only expand when collapsed
const handleBubbleClick = () => {
  if (!isExpanded.value) {
    isExpanded.value = true
  }
}

// Check if we have any lights connected (Hue bridges or WLED devices)
const hasLights = computed(() => 
  hueBridgesStore.allBridges.length > 0 || lightsStore.allLights.length > 0
)

onMounted(async () => {
  await hueBridgesStore.fetchBridges()
  await lightsStore.fetchLights()
})
</script>

<style scoped>
/* Bubble animation handled by Bubble component */
</style>
